import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import { handleErrorToast, handleSuccessToast } from 'src/components/molecules';
import { UploadScreen } from 'src/components/pages/UploadUsers/UploadUsers';
import {
  BulkReinviteCreateUser,
  ValidateUsersCsvUploadMutation,
  useBulkReinviteMutation,
  useValidateUsersCsvUploadMutation,
} from 'src/graphql';
import { CsvImportSuccess, FeatureFlag, State } from 'src/types';
import {
  trackBulkReinviteFailed,
  trackBulkUploadFailed,
  trackBulkUploadSuccess,
} from 'src/utils/analytics';

export const useUploadUsers = () => {
  const [loading, setLoading] = useState(false);
  const [redirect, setRedirect] = useState(false);
  const [data, setData] = useState<
    ValidateUsersCsvUploadMutation['validateUsersCsvUpload']
  >({
    totalUsersInCSV: 0,
    usersToUpdate: [],
    usersToCreate: [],
    usersToReinvite: [],
    usersToDeactivate: [],
  });
  const [screen, setScreen] = useState(UploadScreen.UPLOAD);
  const organisation_id = useSelector(
    ({ organisation }: State) => organisation.organisation?.id,
  );

  const features = useSelector(({ features }: State) => features);

  const loggedInUser = useSelector(
    ({ globalUsers }: State) => globalUsers.currentUser,
  );

  const filteredUsersToDeactivate = useMemo(() => {
    return data.usersToDeactivate.filter(
      (user) => user.id.toString() !== loggedInUser?.id?.toString(),
    );
  }, [data.usersToDeactivate, loggedInUser]);

  const segmentEnabled = isRequiredFeatureEnabled(
    features,
    FeatureFlag.FRONTEND_SEGMENT_TRACKING,
  );

  const { mutate: upload, isSuccess: isValidated } =
    useValidateUsersCsvUploadMutation({
      onMutate: () => {
        setLoading(true);
      },
      onSuccess: (uploadedData) => {
        setData(uploadedData.validateUsersCsvUpload);
        setLoading(false);
      },
      onError: () => {
        if (segmentEnabled && organisation_id) {
          trackBulkReinviteFailed({ organisation_id });
        }
        handleErrorToast({
          toastMessage: 'Oops!',
          toastDescription:
            'We encountered a problem while uploading your CSV. Please try again.',
        });
        setLoading(false);
      },
    });

  const onImportSuccess = (data: CsvImportSuccess) => {
    upload({
      newUsers: [
        ...data.records,
        ...data.error_records.map(({ data }) => data),
      ] as BulkReinviteCreateUser[],
      sheetId: data.sheet_id,
      embedId: data.embed_id,
    });
  };

  const { mutate: bulkReinvite } = useBulkReinviteMutation({
    onSuccess: () => {
      if (segmentEnabled && organisation_id) {
        trackBulkUploadSuccess({ organisation_id });
      }
      handleSuccessToast({
        toastMessage: 'Congratulations!',
        toastDescription: 'You have successfully updated your users.',
      });
    },
    onError: () => {
      if (segmentEnabled && organisation_id) {
        trackBulkUploadFailed({ organisation_id });
      }
      handleErrorToast({
        toastMessage: 'Oops!',
        toastDescription:
          'We encountered a problem while updating your users. Please try again.',
      });
    },
  });

  const commit = (activate: boolean, deactivate: boolean) => {
    return bulkReinvite({
      usersToUpdate: data.usersToUpdate,
      usersToCreate: data.usersToCreate.map((user) => ({
        ...user,
        activateUser: activate,
      })),
      usersToReinvite: activate ? data.usersToReinvite : [],
      usersToDeactivate: deactivate ? filteredUsersToDeactivate : [],
    });
  };

  const cancel = () => {
    setData({
      totalUsersInCSV: 0,
      usersToUpdate: [],
      usersToCreate: [],
      usersToReinvite: [],
      usersToDeactivate: [],
    });
    setRedirect(false);
    setLoading(false);
    setScreen(UploadScreen.UPLOAD);
  };

  return {
    loading,
    redirect,
    setRedirect,
    screen,
    onImportSuccess,
    cancel,
    totalUsersInCSV: data.totalUsersInCSV,
    usersToCreate: data.usersToCreate,
    usersToReinvite: data.usersToReinvite,
    usersToDeactivate: filteredUsersToDeactivate,
    usersToUpdate: data.usersToUpdate,
    setScreen,
    commit,
    isValidated,
  };
};
